/** @format */

import React, { useMemo, useState, useEffect, useRef } from "react";

type Primitive = string | number | boolean;

interface ColumnFilterMenuProps {
  columnKey: string;
  allValues: Primitive[];
  selected: Set<Primitive> | undefined;
  onChange: (nextSelected: Set<Primitive>) => void;
  onClose: () => void;
  dark?: boolean;
}

const ColumnFilterMenu: React.FC<ColumnFilterMenuProps> = ({
  columnKey,
  allValues,
  selected,
  onChange,
  onClose,
  dark,
}) => {
  const [query, setQuery] = useState("");
  const menuRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    // Add event listener
    document.addEventListener("mousedown", handleClickOutside);

    // Cleanup
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  const values = useMemo(() => {
    const set = new Set<Primitive>();
    for (const v of allValues) set.add(v);
    const arr = Array.from(set);
    arr.sort((a, b) =>
      String(a).localeCompare(String(b), undefined, {
        numeric: true,
        sensitivity: "base",
      })
    );
    return arr;
  }, [allValues]);

  const filtered = useMemo(() => {
    if (!query.trim()) return values;
    const q = query.toLowerCase().trim();
    return values.filter((v) => String(v).toLowerCase().includes(q));
  }, [values, query]);

  const isAllChecked =
    selected && selected.size > 0 && values.every((v) => selected.has(v));

  const toggle = (v: Primitive) => {
    const next = new Set(selected ?? []);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    onChange(next);
  };

  const toggleAll = () => {
    if (isAllChecked) {
      onChange(new Set());
    } else {
      onChange(new Set(values));
    }
  };

  return (
    <div
      ref={menuRef}
      className={`vg-filter-menu ${dark ? "dark" : "light"}`}
      role="dialog"
      aria-label={`Filter ${columnKey}`}
    >
      <input
        className="vg-filter-search"
        placeholder="Search..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        autoFocus
      />
      <div className="vg-filter-list">
        <label className="vg-filter-item">
          <input
            type="checkbox"
            checked={!!isAllChecked}
            onChange={toggleAll}
          />
          <span>(Select All)</span>
        </label>
        {filtered.map((v) => (
          <label key={String(v)} className="vg-filter-item">
            <input
              type="checkbox"
              checked={selected ? selected.has(v) : false}
              onChange={() => toggle(v)}
            />
            <span>{String(v)}</span>
          </label>
        ))}
      </div>
      <div className="vg-filter-actions">
        <button className="vg-btn ghost" onClick={() => onChange(new Set())}>
          Clear
        </button>
        <button className="vg-btn primary" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
};

export default ColumnFilterMenu;
